import { useState, type ReactNode } from 'react';
import {
  useInviteMember,
  useRemoveMember,
  useTenantMembers,
  useUpdateMember,
} from '../../api/tenants';
import { useDeactivateUser, useReactivateUser } from '../../api/users';
import { errorMessage } from '../../api/client';
import {
  displayName,
  ROLE_LABELS,
  TENANT_MAX_MEMBERS,
  type TenantMember,
  type TenantRole,
} from '../../api/types';
import {
  Alert,
  Badge,
  Button,
  Card,
  SelectField,
  TextField,
  formatDate,
  statusLabel,
  statusTone,
} from '../ui';
import { ErrorState, SkeletonTable } from '../states';
import { DataTable } from '../table/DataTable';

/**
 * The accounts on a tenant, with invite / role change / remove for callers
 * who may manage them. Used by the tenant portal (own tenant) and the staff
 * console (selected tenant).
 */
export function TenantTeam({
  tenantId,
  canManage,
  canDeactivate = false,
  selfUserId,
}: {
  tenantId: string;
  canManage: boolean;
  /** Staff console only: switch an account off without removing it. */
  canDeactivate?: boolean;
  selfUserId: string;
}) {
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: ReactNode } | null>(null);
  const membersQuery = useTenantMembers(tenantId);
  const members = membersQuery.data ?? null;

  const count = members?.length ?? 0;
  const full = count >= TENANT_MAX_MEMBERS;

  return (
    <Card
      title="Team"
      subtitle={members ? `${count} of ${TENANT_MAX_MEMBERS} accounts` : undefined}
      className="card--table"
    >
      {notice ? (
        <div style={{ padding: '0 1.25rem' }}>
          <Alert kind={notice.kind}>{notice.text}</Alert>
        </div>
      ) : null}
      {membersQuery.isError ? (
        <ErrorState
          error={membersQuery.error}
          onRetry={() => membersQuery.refetch()}
          retrying={membersQuery.isFetching}
        />
      ) : null}
      {membersQuery.isPending ? <SkeletonTable rows={2} cols={5} /> : null}
      {members ? (
        <DataTable
          label="Team accounts"
          columns={[
            { key: 'name', header: 'Name' },
            { key: 'email', header: 'Email' },
            { key: 'role', header: 'Role' },
            { key: 'status', header: 'Status' },
            { key: 'added', header: 'Added' },
            { key: 'actions', header: 'Actions', srOnly: true },
          ]}
        >
          {members.map((m) => (
            <MemberRow
              key={m.user.id}
              tenantId={tenantId}
              member={m}
              canManage={canManage}
              canDeactivate={canDeactivate}
              isSelf={m.user.id === selfUserId}
              onChanged={(text, kind = 'success') => setNotice({ kind, text })}
            />
          ))}
        </DataTable>
      ) : null}
      {canManage && members ? (
        full ? (
          <p className="muted" style={{ padding: '0.75rem 1.25rem' }}>
            All {TENANT_MAX_MEMBERS} accounts are in use. Remove one to invite someone else.
          </p>
        ) : (
          <InviteMemberForm
            tenantId={tenantId}
            onDone={(m) =>
              setNotice({
                kind: 'success',
                text: (
                  <>
                    Invited <strong>{displayName(m.user)}</strong> as{' '}
                    {ROLE_LABELS[m.membership.memberRole]}. A temporary password is on its way to{' '}
                    {m.user.email}.
                  </>
                ),
              })
            }
          />
        )
      ) : null}
    </Card>
  );
}

function InviteMemberForm({
  tenantId,
  onDone,
}: {
  tenantId: string;
  onDone: (m: TenantMember) => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TenantRole>('member');

  const invite = useInviteMember(tenantId);
  const submit = () =>
    invite.mutate(
      {
        email: email.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        role,
      },
      {
        onSuccess: (m) => {
          setFirstName('');
          setLastName('');
          setEmail('');
          onDone(m);
        },
      },
    );

  return (
    <form
      className="form form--grid"
      style={{ padding: '0.75rem 1.25rem 1.25rem' }}
      onSubmit={(e) => {
        e.preventDefault();
        submit();
      }}
      noValidate
      aria-label="Invite a team member"
    >
      {invite.error ? (
        <div className="form__span">
          <Alert kind="error">{errorMessage(invite.error)}</Alert>
        </div>
      ) : null}
      <TextField
        label="First name"
        name="memberFirstName"
        autoComplete="off"
        required
        value={firstName}
        onChange={(e) => setFirstName(e.target.value)}
      />
      <TextField
        label="Last name"
        name="memberLastName"
        autoComplete="off"
        required
        value={lastName}
        onChange={(e) => setLastName(e.target.value)}
      />
      <TextField
        label="Email"
        name="memberEmail"
        type="email"
        autoComplete="off"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <SelectField
        label="Role"
        value={role}
        onChange={(e) => setRole(e.target.value as TenantRole)}
      >
        <option value="member">Member</option>
        <option value="owner">Owner</option>
      </SelectField>
      <div className="form__span">
        <Button
          type="submit"
          variant="secondary"
          loading={invite.isPending}
          disabled={!firstName.trim() || !lastName.trim() || !email.trim()}
        >
          Invite
        </Button>
      </div>
    </form>
  );
}

function MemberRow({
  tenantId,
  member,
  canManage,
  canDeactivate,
  isSelf,
  onChanged,
}: {
  tenantId: string;
  member: TenantMember;
  canManage: boolean;
  canDeactivate: boolean;
  isSelf: boolean;
  onChanged: (text: ReactNode, kind?: 'success' | 'error') => void;
}) {
  const [confirming, setConfirming] = useState(false);
  const roleChange = useUpdateMember(tenantId);
  const remove = useRemoveMember(tenantId);
  const deactivate = useDeactivateUser();
  const reactivate = useReactivateUser();
  const toggle = member.user.isDeactivated ? reactivate : deactivate;

  const changeRole = () => {
    const next: TenantRole = member.membership.memberRole === 'owner' ? 'member' : 'owner';
    roleChange.mutate(
      { userId: member.user.id, role: next },
      {
        onSuccess: () =>
          onChanged(
            <>
              <strong>{displayName(member.user)}</strong> is now {ROLE_LABELS[next]}.
            </>,
          ),
      },
    );
  };
  const confirmRemove = () =>
    remove.mutate(member.user.id, {
      onSuccess: () => {
        setConfirming(false);
        onChanged(
          <>
            Removed <strong>{displayName(member.user)}</strong>. Their sign-in is disabled.
          </>,
        );
      },
    });
  const toggleActive = () =>
    toggle.mutate(member.user.id, {
      onSuccess: (next) =>
        onChanged(
          <>
            <strong>{displayName(next)}</strong>{' '}
            {next.isDeactivated ? 'is deactivated' : 'is active again'}.
          </>,
        ),
    });
  const failure = roleChange.error ?? remove.error ?? toggle.error;
  const error = failure ? errorMessage(failure) : null;

  return (
    <>
      <tr>
        <td>
          {displayName(member.user)}
          {isSelf ? <span className="muted"> (you)</span> : null}
        </td>
        <td>{member.user.email}</td>
        <td>
          <Badge tone={member.membership.memberRole === 'owner' ? 'accent' : 'neutral'}>
            {ROLE_LABELS[member.membership.memberRole]}
          </Badge>
        </td>
        <td>
          {member.user.isDeactivated ? (
            <Badge tone="bad">Deactivated</Badge>
          ) : (
            <Badge tone={member.user.isProviderEnabled ? statusTone(member.user.status) : 'bad'}>
              {member.user.isProviderEnabled ? statusLabel(member.user.status) : 'Sign-in disabled'}
            </Badge>
          )}
        </td>
        <td>{formatDate(member.membership.createdAt)}</td>
        {canManage ? (
          <td>
            <div className="row-actions">
              {canDeactivate ? (
                <Button
                  variant="ghost"
                  className="btn--small"
                  loading={toggle.isPending}
                  onClick={toggleActive}
                  aria-label={`${member.user.isDeactivated ? 'Reactivate' : 'Deactivate'} ${displayName(member.user)}`}
                >
                  {member.user.isDeactivated ? 'Reactivate' : 'Deactivate'}
                </Button>
              ) : null}
              <Button
                variant="ghost"
                className="btn--small"
                loading={roleChange.isPending}
                disabled={isSelf}
                onClick={changeRole}
              >
                {member.membership.memberRole === 'owner' ? 'Make member' : 'Make owner'}
              </Button>
              <Button
                variant="secondary"
                className="btn--small"
                disabled={isSelf || confirming}
                onClick={() => setConfirming(true)}
                aria-label={`Remove ${displayName(member.user)}`}
              >
                Remove…
              </Button>
            </div>
          </td>
        ) : null}
      </tr>
      {error ? (
        <tr className="table__detail">
          <td colSpan={canManage ? 6 : 5}>
            <Alert kind="error">{error}</Alert>
          </td>
        </tr>
      ) : null}
      {confirming ? (
        <tr className="table__detail">
          <td colSpan={6} style={{ padding: 0 }}>
            <div
              className="confirm"
              role="group"
              aria-label={`Confirm removing ${displayName(member.user)}`}
            >
              <span>
                Remove <strong>{displayName(member.user)}</strong>? Their account is deleted and
                sign-in disabled.
              </span>
              <Button
                variant="danger"
                className="btn--small"
                loading={remove.isPending}
                onClick={confirmRemove}
              >
                Confirm remove
              </Button>
              <Button
                variant="ghost"
                className="btn--small"
                onClick={() => setConfirming(false)}
                disabled={remove.isPending}
              >
                Cancel
              </Button>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}
